import { Link } from 'react-router-dom';
import { 
  Phone, MapPin, Clock, ArrowLeft, 
  CreditCard, Banknote, UtensilsCrossed, 
  ShoppingBag, Accessibility, Ticket
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { restaurantInfo } from '../data/menuData';

// Icon mapping
const iconMap = {
  CreditCard,
  Banknote,
  UtensilsCrossed,
  ShoppingBag,
  Accessibility,
  Ticket
};

// Hours Table Component
const HoursTable = () => {
  const days = [
    { key: 'monday', label: 'Lundi' },
    { key: 'tuesday', label: 'Mardi' },
    { key: 'wednesday', label: 'Mercredi' },
    { key: 'thursday', label: 'Jeudi' },
    { key: 'friday', label: 'Vendredi' },
    { key: 'saturday', label: 'Samedi' },
    { key: 'sunday', label: 'Dimanche' },
  ];

  return (
    <div className="space-y-2" data-testid="hours-table">
      {days.map(({ key, label }) => {
        const hours = restaurantInfo.hours[key];
        return (
          <div 
            key={key}
            className="flex justify-between items-center py-2 border-b border-white/5 last:border-0"
          >
            <span className="font-accent text-white uppercase tracking-wider">{label}</span>
            <div className="text-right text-gray-400">
              {hours.lunch && hours.dinner ? (
                <span>{hours.lunch} / {hours.dinner}</span>
              ) : hours.dinner ? (
                <span>{hours.dinner}</span>
              ) : (
                <span className="text-gray-600">Fermé</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Feature Badge
const FeatureBadge = ({ icon, text }) => {
  const IconComponent = iconMap[icon] || CreditCard;
  return (
    <div className="flex items-center gap-3 p-3 bg-[#0f0f0f] rounded-lg border border-white/5">
      <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center flex-shrink-0">
        <IconComponent className="w-5 h-5 text-orange-500" />
      </div>
      <span className="text-gray-300 text-sm">{text}</span>
    </div>
  );
};

export const InfoPage = () => {
  return (
    <div className="min-h-screen bg-[#050505] pt-20 md:pt-24" data-testid="info-page">
      {/* Header */}
      <div className="px-4 md:px-8 py-8 md:py-12 bg-[#0A0A0A] border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4" data-testid="info-back-to-home">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Retour</span>
          </Link>
          <h1 className="font-heading text-4xl md:text-5xl text-white uppercase mb-2">
            Nos <span className="text-orange-500">Infos</span>
          </h1>
          <p className="text-gray-400">
            Tout ce qu'il faut savoir pour nous rendre visite
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 md:px-8 py-8 md:py-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Left Column - Contact & Location */}
            <div className="space-y-8">
              {/* Contact Card */}
              <div className="info-card p-6 md:p-8 rounded-xl border border-white/5" data-testid="contact-card">
                <h2 className="font-heading text-2xl text-white uppercase mb-6 flex items-center gap-3">
                  <Phone className="w-6 h-6 text-orange-500" />
                  Contact
                </h2>
                
                <div className="space-y-4">
                  <a 
                    href={restaurantInfo.phoneLink}
                    className="block p-4 bg-green-600/10 border border-green-500/30 rounded-xl hover:bg-green-600/20 transition-colors"
                    data-testid="info-phone-link"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-green-600 flex items-center justify-center">
                        <Phone className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm mb-1">Appelez-nous</p>
                        <p className="font-heading text-2xl text-white">{restaurantInfo.phone}</p>
                      </div>
                    </div>
                  </a>
                </div>
              </div>

              {/* Location Card */}
              <div className="info-card p-6 md:p-8 rounded-xl border border-white/5" data-testid="location-card">
                <h2 className="font-heading text-2xl text-white uppercase mb-6 flex items-center gap-3">
                  <MapPin className="w-6 h-6 text-orange-500" />
                  Adresse
                </h2>
                
                <div className="space-y-4">
                  <div className="p-4 bg-[#0f0f0f] rounded-xl">
                    <p className="font-accent text-lg text-white mb-1">{restaurantInfo.address}</p>
                    <p className="text-gray-400">{restaurantInfo.city}</p>
                    <p className="text-gray-500 text-sm">{restaurantInfo.region}</p>
                  </div>

                  {/* Google Maps Embed */}
                  <div className="rounded-xl overflow-hidden h-64 border border-white/10">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2607.5!2d1.8853!3d49.2661!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e6a3f5d5e5e5e5%3A0x0!2s10%20Rue%20Roger%20Blondeau%2C%2060240%20Chaumont-en-Vexin!5e0!3m2!1sfr!2sfr!4v1699999999999!5m2!1sfr!2sfr"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Localisation Délices de Chaumont"
                    ></iframe>
                  </div>

                  {/* Nearby Areas */}
                  <div className="mt-4">
                    <p className="text-gray-500 text-sm mb-2">Nous servons également :</p>
                    <div className="flex flex-wrap gap-2">
                      {restaurantInfo.nearbyAreas.map((area) => (
                        <span 
                          key={area}
                          className="px-3 py-1 bg-[#0f0f0f] rounded-full text-xs text-gray-400 border border-white/5"
                        >
                          {area}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Hours & Features */}
            <div className="space-y-8">
              {/* Hours Card */}
              <div className="info-card p-6 md:p-8 rounded-xl border border-white/5" data-testid="hours-card">
                <h2 className="font-heading text-2xl text-white uppercase mb-6 flex items-center gap-3">
                  <Clock className="w-6 h-6 text-orange-500" />
                  Horaires d'ouverture
                </h2>
                
                <HoursTable />
                
                <div className="mt-6 p-4 bg-orange-500/10 rounded-xl border border-orange-500/20">
                  <p className="text-orange-400 text-sm">
                    <strong>Commandes :</strong> Passez votre commande par téléphone pour un retrait rapide !
                  </p>
                </div>
              </div>

              {/* Features Card */}
              <div className="info-card p-6 md:p-8 rounded-xl border border-white/5" data-testid="features-card">
                <h2 className="font-heading text-2xl text-white uppercase mb-6">
                  Informations pratiques
                </h2>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {restaurantInfo.features.map((feature, index) => (
                    <FeatureBadge key={index} icon={feature.icon} text={feature.text} />
                  ))}
                </div>
              </div>

              {/* Google Business Link */}
              <div className="info-card p-6 md:p-8 rounded-xl border border-white/5 bg-gradient-to-br from-orange-500/5 to-transparent" data-testid="google-card">
                <h2 className="font-heading text-2xl text-white uppercase mb-4">
                  Avis Google
                </h2>
                <p className="text-gray-400 mb-4">Consultez nos avis et laissez le vôtre !</p>
                <a 
                  href={restaurantInfo.googleMapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-black font-bold uppercase tracking-wider rounded-md transition-all hover:scale-105"
                  data-testid="google-business-link"
                >
                  <MapPin className="w-5 h-5" />
                  Voir sur Google
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="px-4 md:px-8 py-12 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white uppercase mb-4">
            Envie de <span className="text-orange-500">Délices</span> ?
          </h2>
          <p className="text-gray-400 mb-6">
            Appelez-nous dès maintenant pour passer commande
          </p>
          <a href={restaurantInfo.phoneLink} data-testid="info-bottom-call-button">
            <Button className="bg-orange-500 hover:bg-orange-600 text-black font-bold uppercase tracking-wider px-10 py-5 rounded-md flex items-center gap-3 mx-auto text-lg glow-orange">
              <Phone className="w-6 h-6" />
              {restaurantInfo.phone}
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
};

export default InfoPage;
